import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import { SiteLanguageProvider } from './context/SiteLanguageContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import About from './pages/About';
import HowItWorks from './pages/HowItWorks';
import StudentSupport from './pages/StudentSupport';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import Apply from './pages/Apply';
import EnrolmentSuccess from './pages/EnrolmentSuccess';
import AwardingBody from './pages/AwardingBody';
import UniversityProgression from './pages/UniversityProgression';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsConditions from './pages/legal/TermsConditions';
import CookiePolicy from './pages/legal/CookiePolicy';
import CookieConsent from './components/CookieConsent';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCourses from './pages/admin/AdminCourses';
import AdminCourseForm from './pages/admin/AdminCourseForm';
import AdminEnquiries from './pages/admin/AdminEnquiries';
import AdminBanner from './pages/admin/AdminBanner';
import AdminPromoCodes from './pages/admin/AdminPromoCodes';
import AdminPopupAd from './pages/admin/AdminPopupAd';
import AdminCourseBulkUpload from './pages/admin/AdminCourseBulkUpload';
import WhatsAppChat from './components/WhatsAppChat';
import PopupAd from './components/PopupAd';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppChat />
      <PopupAd />
      <CookieConsent />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!admin) return <Navigate to="/admin" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public site */}
        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/courses" element={<PublicLayout><Courses /></PublicLayout>} />
        <Route path="/courses/:slug" element={<PublicLayout><CourseDetail /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
        <Route path="/how-it-works" element={<PublicLayout><HowItWorks /></PublicLayout>} />
        <Route path="/student-support" element={<PublicLayout><StudentSupport /></PublicLayout>} />
        <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
        <Route path="/apply" element={<PublicLayout><Apply /></PublicLayout>} />
        <Route path="/apply/success" element={<PublicLayout><EnrolmentSuccess /></PublicLayout>} />
        <Route path="/university-progression" element={<PublicLayout><UniversityProgression /></PublicLayout>} />
        <Route path="/awarding-bodies/:code" element={<PublicLayout><AwardingBody /></PublicLayout>} />
        <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
        <Route path="/terms" element={<PublicLayout><TermsConditions /></PublicLayout>} />
        <Route path="/cookie-policy" element={<PublicLayout><CookiePolicy /></PublicLayout>} />

        {/* Admin */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/courses" element={<ProtectedRoute><AdminCourses /></ProtectedRoute>} />
        <Route path="/admin/courses/new" element={<ProtectedRoute><AdminCourseForm /></ProtectedRoute>} />
        <Route path="/admin/courses/bulk-upload" element={<ProtectedRoute><AdminCourseBulkUpload /></ProtectedRoute>} />
        <Route path="/admin/courses/edit/:id" element={<ProtectedRoute><AdminCourseForm /></ProtectedRoute>} />
        <Route path="/admin/enquiries" element={<ProtectedRoute><AdminEnquiries /></ProtectedRoute>} />
        <Route path="/admin/banner" element={<ProtectedRoute><AdminBanner /></ProtectedRoute>} />
        <Route path="/admin/popup-ad" element={<ProtectedRoute><AdminPopupAd /></ProtectedRoute>} />
        <Route path="/admin/promo-codes" element={<ProtectedRoute><AdminPromoCodes /></ProtectedRoute>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SiteLanguageProvider>
        <AdminAuthProvider>
          <AppRoutes />
        </AdminAuthProvider>
      </SiteLanguageProvider>
    </BrowserRouter>
  );
}

export default App;
